import React from 'react';
import { useDarkMode } from '../../../../context/DarkModeContext';
import { 
  DocumentTextIcon, 
  XCircleIcon, 
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline';
import { InternshipResult } from '../../../../types/internship';
import { CareerFocusWithMetrics } from '../../../../hooks/useCareerFocus';

interface OutcomeSelectorProps {
  data: Partial<InternshipResult>;
  onUpdate: (updates: Partial<InternshipResult>) => void;
  focus: CareerFocusWithMetrics;
}

const OutcomeSelector: React.FC<OutcomeSelectorProps> = ({
  data,
  onUpdate
}) => {
  const { darkMode } = useDarkMode();
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-gray-600' : 'border-gray-300';
  const selectedBorderColor = darkMode ? 'border-blue-500' : 'border-blue-500';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
  
  const outcomes = [
    {
      id: 'offer_received' as const,
      icon: DocumentTextIcon,
      title: 'Received Offer',
      description: 'I received a full-time or return offer',
      color: 'text-green-500'
    },
    {
      id: 'no_offer' as const,
      icon: XCircleIcon,
      title: 'Did Not Receive Offer',
      description: 'The internship concluded without an offer',
      color: 'text-red-500'
    },
    {
      id: 'withdrew' as const,
      icon: ArrowRightOnRectangleIcon,
      title: 'Withdrew / Other',
      description: 'I withdrew from the process or other outcome',
      color: 'text-amber-500'
    }
  ];
  
  const handleSelect = (outcomeType: InternshipResult['outcomeType']) => {
    onUpdate({ outcomeType });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <h2 className={`text-xl font-semibold mb-2 ${textColor}`}>
          How did your internship conclude?
        </h2>
        <p className={mutedTextColor}>
          Select the outcome that best describes your situation
        </p>
      </div>
      
      <div className="grid gap-4 mt-6">
        {outcomes.map((outcome) => {
          const Icon = outcome.icon;
          const isSelected = data.outcomeType === outcome.id;
          
          return (
            <button
              key={outcome.id}
              type="button"
              onClick={() => handleSelect(outcome.id)}
              className={`
                p-6 rounded-lg border-2 transition-all text-left
                ${isSelected ? selectedBorderColor : borderColor}
                ${bgColor} ${hoverBg}
              `}
            >
              <div className="flex items-start gap-4">
                <div className={`mt-1 ${outcome.color}`}>
                  <Icon className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className={`font-medium text-lg mb-1 ${textColor}`}>
                    {outcome.title}
                  </h3>
                  <p className={`text-sm ${mutedTextColor}`}>
                    {outcome.description}
                  </p>
                </div>
                {isSelected && (
                  <div className="mt-1">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default OutcomeSelector;